/**
 * Shared helpers for player data display — used by both PlayerView and
 * PlayerProfileView to avoid duplicating formatting and class-list logic.
 */

const UNIT_IMG = 'https://luxor-fftbg-assets.s3.amazonaws.com/units/';

/** Format a timestamp (ms or Date) to "Jun 3, 2025" style. */
export function fmtDate(ms) {
  return new Date(ms).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Format a gil amount to compact "1.2M" / "340k" / "500" form. */
export function fmtGil(n) {
  const a = Math.abs(n);
  if (a >= 1e6) return +(n / 1e6).toFixed(1) + 'M';
  if (a >= 1e3) return Math.round(n / 1e3) + 'k';
  return '' + Math.round(n);
}

/**
 * Per-class win-rate breakdown for a player, sorted by games played (desc).
 * Each entry: { name, played, won, wr (%), pct (bar width vs most-played) }.
 */
export function classList(p) {
  const ents = Object.entries(p.classes).sort((a, b) => b[1][0] - a[1][0]);
  const max = ents.length ? ents[0][1][0] : 1;
  return ents.map(([name, [pl, wo]]) => ({ name, played: pl, won: wo, wr: Math.round((wo / pl) * 100), pct: Math.round((pl / max) * 100) }));
}

/**
 * Classes the player has championed, sorted by title count (desc).
 * Each entry: { name, n (title count) }.
 */
export function champClassList(p) {
  return Object.entries(p.champClasses || {}).map(([name, a]) => ({ name, n: a[0] })).sort((x, y) => y.n - x.n);
}

/**
 * Best and worst class by win rate (min 20 games).
 * Returns { best: {name, wr}, worst: {name, wr} } or null if <2 qualifying.
 */
export function bestWorst(p) {
  const cls = classList(p).filter((c) => c.played >= 20);
  if (cls.length < 2) return null;
  const byWr = [...cls].sort((a, b) => b.wr - a.wr);
  return { best: byWr[0], worst: byWr[byWr.length - 1] };
}

/**
 * Build the class icon URL from class name + the classAgg gender aggregation.
 * classAgg[cls] = [maleChamps, femaleChamps] — [0,0] = monster (no gender suffix).
 */
export function classIcon(cls, classAgg) {
  const ag = (classAgg && classAgg[cls]) || [0, 0];
  return UNIT_IMG + cls + (ag[0] === 0 && ag[1] === 0 ? '' : (ag[0] > 0 ? 'M' : 'F')) + '.gif';
}

/**
 * Build classAgg from a loaded players array: { className: [maleChamps, femaleChamps] }.
 * Used by both PlayerView and PlayerProfileView to derive monster/human class lists.
 */
export function buildClassAgg(players) {
  const agg = {};
  for (const p of players) {
    if (!p.champClasses) continue;
    for (const cls in p.champClasses) {
      const a = p.champClasses[cls];
      agg[cls] = agg[cls] || [0, 0];
      agg[cls][0] += a[1];  // male
      agg[cls][1] += a[2];  // female
    }
  }
  return agg;
}

/** Monster classes = no one ever championed them as M/F. */
export function monsterClasses(classAgg) {
  return Object.keys(classAgg).filter((c) => classAgg[c][0] === 0 && classAgg[c][1] === 0).sort();
}

/** Human classes = someone has championed them as male or female. */
export function humanClasses(classAgg) {
  return Object.keys(classAgg).filter((c) => classAgg[c][0] > 0 || classAgg[c][1] > 0).sort();
}

/** The profile's player object: a playerIndex record with its optional fields defaulted. */
export function playerFromRecord(name, record) {
  return {
    name,
    played: record.played,
    won: record.won,
    winRate: record.winRate,
    classes: record.classes || {},
    champ: record.champ || 0,
    champDef: record.champDef || 0,
    champReign: record.champReign || 0,
    firstChamp: record.firstChamp || null,
    lastChamp: record.lastChamp || null,
    champClasses: record.champClasses || null,
    champSigns: record.champSigns || null,
    champColors: record.champColors || null,
    champYears: record.champYears || null,
    mqDates: record.mqDates || null,
    tw: record.tw || 0,
    rd: record.rd || null,  // per-round [played, won] pairs (lib/roundRecord.js)
  };
}

/**
 * A player's bettingStats record, or null. Betting keys are canonKey form (lowercase,
 * spaces→underscores, e.g. "run_with_stone_guns"); the display name has spaces. Twitch
 * can't show underscores so FFTBG substitutes spaces — but not 1:1 (trailing/double
 * underscores survive in the bet log as "maakur_" / "red__lancer" while the roster
 * trims/keeps them as "Maakur" / "Red Lancer"). Collapse every run of whitespace/
 * underscore to one space and trim, so all variants match.
 */
export function bettingFor(bettingStats, name) {
  const norm = (s) => s.toLowerCase().replace(/[\s_]+/g, ' ').trim();
  return bettingStats[name] || bettingStats[Object.keys(bettingStats).find((k) => norm(k) === norm(name))] || null;
}

/** Detailed tenure span, e.g. "2y 3mo 14d". */
export function spanDetailed(p) {
  const a = new Date(p.firstChamp), b = new Date(p.lastChamp);
  let y = b.getFullYear() - a.getFullYear(), m = b.getMonth() - a.getMonth(), d = b.getDate() - a.getDate();
  if (d < 0) { m -= 1; d += new Date(b.getFullYear(), b.getMonth(), 0).getDate(); }
  if (m < 0) { y -= 1; m += 12; }
  const parts = [];
  if (y) parts.push(y + 'y');
  if (m) parts.push(m + 'mo');
  if (d || !parts.length) parts.push(d + 'd');
  return parts.join(' ');
}

/** Win-rate bar style — zoom the 35–65% range to 0–100% width. */
export function barStyle(wr) {
  return { width: Math.max(2, Math.min(100, (wr - 35) * (100 / 30))) + '%' };
}

/** Gender-locked classes — only one sprite exists (Bard ♂, Dancer ♀). */
export const GENDER_LOCK = { Dancer: 'F', Bard: 'M' };

/** ordinal(1)='1st', 2='2nd', 11='11th', 23='23rd', … */
export function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Portrait FILE name (no base URL): ClassGender.gif for humans, Class.gif for
 * monsters; gender-locked classes use their fixed sprite. gender = 'M'|'F'|'Monster'.
 * Prepend an asset base (e.g. UNITS_BASE) to use it.
 */
export function classPortrait(name, gender) {
  if (gender === 'Monster') return name + '.gif';
  if (GENDER_LOCK[name]) return name + GENDER_LOCK[name] + '.gif';
  return name + (gender || 'M') + '.gif';
}

export { UNIT_IMG };
